<template>
  <a-layout>
    <drawer v-if="isMobile" :openDrawer="drawerOpen" @change="onDrawerChange">
      <sider-menu :theme="theme" :menuData="menuData" :collapsed="false" :collapsible="false" @menuSelect="onMenuSelect"/>
    </drawer>
    <!-- 窄屏（<768px）时保留收起态（图标）侧边栏，宽屏跟随 store 折叠状态；汉堡按钮另行打开完整菜单抽屉 -->
    <sider-menu v-if="layout === 'side'" :theme="theme" :menuData="menuData" :collapsed="isMobile || !sidebarOpened" :collapsible="true" />
    <drawer :open-drawer="settingBar" placement="right">
      <setting />
    </drawer>
    <a-layout :style="{ paddingLeft: paddingLeft }">
      <global-header :menuData="menuData" :collapsed="isMobile ? drawerOpen : !sidebarOpened" @toggleCollapse="toggleCollapse"/>
      <a-layout-content :style="{minHeight: minHeight, margin: '20px 14px 0'}" :class="fixHeader ? 'fixed-header-content' : null">
        <slot></slot>
      </a-layout-content>
      <a-layout-footer style="padding: .29rem 0" class="copyright">
        <global-footer :copyright="copyright"/>
      </a-layout-footer>
    </a-layout>
  </a-layout>
</template>

<script>
import GlobalHeader from './GlobalHeader'
import GlobalFooter from './GlobalFooter'
import Drawer from '~/tool/Drawer'
import SiderMenu from '~/menu/SiderMenu'
import Setting from '~/setting/Setting'
import { mapState, mapMutations } from 'vuex'
import { triggerWindowResizeEvent } from 'utils/common'

const minHeight = window.innerHeight - 64 - 24 - 66

let menuData = []

export default {
  name: 'GlobalLayout',
  components: {Setting, SiderMenu, Drawer, GlobalFooter, GlobalHeader},
  data () {
    return {
      minHeight: minHeight + 'px',
      drawerOpen: false,
      menuData: menuData
    }
  },
  computed: {
    paddingLeft () {
      if (this.fixSiderbar && this.layout === 'side') {
        // 窄屏强制收起态：固定留 80px
        if (this.isMobile) {
          return '80px'
        }
        return this.sidebarOpened ? '256px' : '80px'
      }
      return '0'
    },
    ...mapState({
      sidebarOpened: state => state.setting.sidebar.opened,
      isMobile: state => state.setting.isMobile,
      theme: state => state.setting.theme,
      layout: state => state.setting.layout,
      copyright: state => state.setting.copyright,
      fixSiderbar: state => state.setting.fixSiderbar,
      fixHeader: state => state.setting.fixHeader,
      settingBar: state => state.setting.settingBar.opened
    })
  },
  watch: {
    // 移动端/桌面端切换时（双向）关闭菜单抽屉，避免任何残留状态导致抽屉自动打开
    isMobile (val) {
      this.drawerOpen = false
      // [layout-probe] 临时探针
      console.info('[layout-probe] isMobile ->', val, 'innerWidth=', window.innerWidth)
      this.dumpDom('isMobile=' + val)
    },
    // [layout-probe] 临时探针：store 折叠状态变化
    sidebarOpened (val) {
      console.info('[layout-probe] sidebarOpened ->', val, 'innerWidth=', window.innerWidth)
      this.dumpDom('sidebarOpened=' + val)
    },
    // [layout-probe] 临时探针：抽屉状态变化
    drawerOpen (val) {
      console.info('[layout-probe] drawerOpen ->', val, 'innerWidth=', window.innerWidth)
      this.dumpDom('drawerOpen=' + val)
    }
  },
  mounted () {
    // [layout-probe] 临时探针：确认浏览器加载的是最新 bundle
    console.info('[layout-probe] R3 build loaded. isMobile=', this.isMobile, 'sidebarOpened=', this.sidebarOpened, 'innerWidth=', window.innerWidth)
    this.dumpDom('mounted')
    setTimeout(() => this.dumpDom('mounted+1s'), 1000)
  },
  methods: {
    ...mapMutations({setSidebar: 'setting/setSidebar'}),
    // [layout-probe] 临时探针：打印真实 DOM 结构（桌面侧边栏容器/抽屉/遮罩/sider 宽度与位置）
    dumpDom (tag) {
      this.$nextTick(() => {
        const wrap = document.querySelector('.desktop-sider-wrap')
        const drawers = Array.from(document.querySelectorAll('.drawer')).map(d => {
          const cs = getComputedStyle(d)
          return d.className.replace(/\s+/g, '.') + '@x' + Math.round(d.getBoundingClientRect().x) + ' pos=' + cs.position + ' tf=' + (cs.transform === 'none' ? 'none' : cs.transform) + ' dv=' + Array.from(d.attributes).filter(a => a.name.indexOf('data-v-') === 0).map(a => a.name.slice(7)).join(',')
        })
        const siders = Array.from(document.querySelectorAll('aside.ant-layout-sider')).map(s => Math.round(s.getBoundingClientRect().width) + 'w@x' + Math.round(s.getBoundingClientRect().x) + ' pos=' + getComputedStyle(s).position)
        const masks = Array.from(document.querySelectorAll('.mask')).map(m => m.className.replace(/\s+/g, '.') + ' disp=' + getComputedStyle(m).display)
        const layouts = document.querySelectorAll('.ant-layout').length
        console.info('[layout-probe] DOM[' + tag + '] wrap=' + (wrap ? 'Y' : 'N'), 'drawers=' + drawers.join('|'), 'siders=' + siders.join('|'), 'masks=' + masks.join('|'), 'antLayoutCount=' + layouts)
      })
    },
    toggleCollapse () {
      // [layout-probe] 临时探针
      console.info('[layout-probe] toggleCollapse, isMobile=', this.isMobile, 'sidebarOpened=', this.sidebarOpened, 'drawerOpen=', this.drawerOpen)
      if (this.isMobile) {
        this.drawerOpen = !this.drawerOpen
        return
      }
      // 桌面端折叠状态统一存放于 store，保证侧边栏宽度与页面 padding 永远同源、不会失步
      this.setSidebar(!this.sidebarOpened)
      triggerWindowResizeEvent()
    },
    onDrawerChange (show) {
      // [layout-probe] 临时探针
      console.info('[layout-probe] onDrawerChange', show)
      this.drawerOpen = show
    },
    onMenuSelect () {
      // [layout-probe] 临时探针
      console.info('[layout-probe] onMenuSelect, isMobile=', this.isMobile)
      if (this.isMobile) {
        this.drawerOpen = false
      } else {
        this.toggleCollapse()
      }
    }
  },
  beforeCreate () {
    let routers = this.$db.get('USER_ROUTER')
    menuData = routers.find((item) => item.path === '/').children.filter((menu) => {
      let meta = menu.meta
      if (typeof meta.isShow === 'undefined') {
        return true
      } else return meta.isShow
    })
  }
}
</script>

<style lang="less" scoped>
  .setting{
    background-color: #1890ff;
    color: #fff;
    border-radius: 5px 0 0 5px;
    line-height: 40px;
    font-size: 22px;
    width: 40px;
    height: 40px;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  }
  .fixed-header-content {
    margin: 76px 12px 0 !important;
  }
</style>



// WEBPACK FOOTER //
// src/views/common/GlobalLayout.vue